import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Select, Space, Tabs, Tooltip, type TabsProps } from 'antd';
import {
  DownloadOutlined,
  ImportOutlined,
  MinusOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import styled from 'styled-components';

import EpiContainer from './components/EpiContainer';
import AddIdmpModal, { type IAddIdmpTab } from './components/AddIdmpModal';
import RemoveIdmpModal, {
  type IRemoveIdmpTab,
} from './components/RemoveIdmpModal';
import ImportEpiModal from './components/ImportEpiModal';
import {
  clearStoredCompositions,
  EpiProvider,
  getStoredCompositions,
} from './context/EpiContext';
import type { IEpiIdmpAdd, IEpiIdmpRemove, IEpiImport } from './context/types';
import { useExportEpi } from './hooks/useExportEpi';
import { useFetchIdmps } from './hooks/useFetchIdmps';
import { useFetchFamilyIdmps } from './hooks/useFetchFamilyIdmps';
import { TAB_CONFIGS } from './types/constants';
import type {
  EpiComposition,
  EpiDocumentData,
  EpiDocumentType,
  EpiExportFormat,
} from './types/types';
import { visibleIdmp } from './utils/schemaUtils';
import { idmpStrength, resolveInstanceName, text } from './utils/epiHelpers';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '../../translate';
import { IModelInstance } from '../../ui-builder';

interface IProps {
  instance: IModelInstance | undefined;
  /**
   * The document to edit, when it is not `instance.data` — a Form field holds
   * its own copy, which a draft or a discard replaces. Always save-ready.
   */
  data?: EpiDocumentData;
  /**
   * Changes whenever the document was replaced from outside, and the editor
   * adopts it. Defaults to the instance's version, which bumps on every save.
   */
  syncKey?: number;
  /** Receives the whole document, save-ready, 500 ms after any change. */
  onChange: (data: EpiDocumentData) => void;
  /**
   * Whether the product set may be changed from here. Both default to true —
   * a screen that omits them keeps the behaviour it had.
   */
  canAddProduct?: boolean;
  canRemoveProduct?: boolean;
  /**
   * View-only. Nothing about the document can be changed: no editor, no
   * section or document menus, no product actions. Language still switches,
   * since reading the other version is not a change.
   */
  readOnly?: boolean;
  /** Whether documents may be split, merged or created. */
  canManageDocuments?: boolean;
  /** Whether a document may be renamed. */
  canRenameDocuments?: boolean;
  /** Whether free-form subsections may be added. */
  canAddCustomSections?: boolean;
  /** Whether the document can be exported as a printable file. */
  canExport?: boolean;
  /**
   * Whether an authored document may be imported over this ePI. Import
   * **replaces** every document on it, so it is off unless a screen asks for
   * it — unlike the other capabilities, which default on.
   */
  canImport?: boolean;
  /**
   * Language the editor opens on. Only a starting point — the author switches
   * freely, and it constrains nothing about what may be stored.
   */
  defaultLanguage?: SupportedLanguage;
}

/**
 * Serbia is the market and `sr` is the language required by the schema, so an
 * unconfigured screen opens there rather than following the admin panel.
 */
const DEFAULT_AUTHORING_LANGUAGE: SupportedLanguage = 'sr';

/** Chips shown before the list collapses behind a "+N more" toggle. */
const VISIBLE_PRODUCTS = 3;

const EXPORT_FORMATS: { value: EpiExportFormat; label: string }[] = [
  { value: 'docx', label: 'Word' },
  { value: 'pdf', label: 'PDF' },
];

/** Chrome is English throughout, this included — it names a language, it is not
 *  written in one. */
const LANGUAGE_LABELS: Record<SupportedLanguage, string> = {
  sr: 'Serbian',
  en: 'English',
};

const Epi = ({
  instance,
  data,
  syncKey = instance?.version,
  onChange,
  canAddProduct = true,
  canRemoveProduct = true,
  readOnly = false,
  canManageDocuments = true,
  canRenameDocuments = true,
  canAddCustomSections = true,
  canExport = true,
  canImport = false,
  defaultLanguage,
}: IProps) => {
  // The language being *edited*, independent of the admin panel's own language:
  // an author may work on the English version of an ePI while the UI is in
  // Serbian. A configured value that isn't a supported language is ignored,
  // since it can arrive from a Variable or JsonLogic setting.
  const [lang, setLang] = useState<SupportedLanguage>(() =>
    defaultLanguage && SUPPORTED_LANGUAGES.includes(defaultLanguage)
      ? defaultLanguage
      : DEFAULT_AUTHORING_LANGUAGE
  );
  const documentId = instance?.id || '';
  // Held in state because adding an IDMP changes the document-level set, and
  // the tabs' providers save whatever is here alongside their compositions.
  const [documentData, setDocumentData] = useState<EpiDocumentData | undefined>(
    data ?? instance?.data
  );
  // The screen refetches the instance after a save, so a new `instance` lands
  // here with a bumped version. Adopt it wholesale: the server assigns its own
  // ids to every array item it creates, and a payload still carrying ours is
  // merged as new items rather than matched — which is how one ePI ended up
  // with the same custom section stored four times.
  //
  // A Form field replaces the document the same way when a draft is loaded or
  // discarded, and bumps `syncKey` for it.
  //
  // The key the tabs are currently seeded from. It is **state**, not the
  // incoming prop: the providers are keyed on it, and keying on the prop would
  // remount them on the render where the key has changed but
  // `documentData` has not, so they would re-seed from the data being replaced
  // and then never re-seed again.
  const [syncedKey, setSyncedKey] = useState(syncKey);
  useEffect(() => {
    if (syncKey === syncedKey) return;
    clearStoredCompositions(documentId);
    // Batched into one re-render, so the remount below sees the new data.
    setDocumentData(data ?? instance?.data);
    setSyncedKey(syncKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncKey, syncedKey, documentId]);

  const [idmpAdd, setIdmpAdd] = useState<IEpiIdmpAdd | null>(null);
  const [idmpRemove, setIdmpRemove] = useState<IEpiIdmpRemove | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState<EpiExportFormat>('docx');
  const [productsOpen, setProductsOpen] = useState(false);
  const { exportEpi, isExporting } = useExportEpi();
  // Only a fallback: the BE names the file from the ePI itself and sends it in
  // Content-Disposition. This covers a response that carries no name.
  const instanceName = resolveInstanceName(documentData?.name, lang) ?? 'epi';
  const [removeOpen, setRemoveOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [epiImport, setEpiImport] = useState<IEpiImport | null>(null);

  const docIdmp = useMemo(
    () => visibleIdmp(documentData?.medicinalProducts ?? []),
    [documentData?.medicinalProducts]
  );
  const { idmpById } = useFetchIdmps(
    useMemo(() => docIdmp.map(r => r.id), [docIdmp])
  );

  // Every reference carries the IDMP model id, so querying the family's
  // members needs nothing else to have loaded first.
  const idmpModelId = docIdmp[0]?.modelId;
  const familyId = documentData?.productFamily?.id;
  const { familyIdmps, isLoadingFamilyIdmps } = useFetchFamilyIdmps(
    addOpen ? familyId : undefined,
    idmpModelId
  );

  const onEpi = useMemo(() => new Set(docIdmp.map(r => r.id)), [docIdmp]);
  const candidates = useMemo(
    () => familyIdmps.filter(i => !onEpi.has(i.id)),
    [familyIdmps, onEpi]
  );

  /** Live compositions per tab, read from the providers' shared store. */
  const addTabs = useMemo<IAddIdmpTab[]>(() => {
    const stored = getStoredCompositions(documentId);
    return TAB_CONFIGS.map(({ key, label }) => {
      const comps = stored[key] ?? documentData?.[key] ?? [];
      return {
        key,
        label,
        compositions: comps
          .filter(c => !c.toBeDeleted)
          .map(c => ({ id: c.id, title: text(c.title, lang) })),
      };
    });
  }, [documentId, documentData, addOpen, lang]);

  /** Live compositions per tab, with the products each covers. */
  const removeTabs = useMemo<IRemoveIdmpTab[]>(() => {
    const stored = getStoredCompositions(documentId);
    return TAB_CONFIGS.map(({ key, label }) => {
      const comps = stored[key] ?? documentData?.[key] ?? [];
      return {
        key,
        label,
        compositions: comps
          .filter(c => !c.toBeDeleted)
          .map(c => ({
            id: c.id,
            title: text(c.title, lang),
            idmpIds: visibleIdmp(c.medicinalProducts).map(r => r.id),
          })),
      };
    });
  }, [documentId, documentData, removeOpen, lang]);

  const namedProducts = useMemo(
    () =>
      docIdmp.map((ref, i) => {
        const idmp = idmpById[ref.id];
        const name =
          resolveInstanceName(idmp?.data?.name, lang) ??
          `Medicinal product ${i + 1}`;
        return {
          id: ref.id,
          name,
          /** Chip text: the strength alone, since the family name repeats. */
          label: idmpStrength(idmp?.data) ?? name,
          // The version this ePI documents, which is not necessarily the latest
          // — the chips resolve names through the current instance.
          version: ref.version,
        };
      }),
    [docIdmp, idmpById, lang]
  );

  /**
   * Takes a product off the ePI. The document-level reference is soft-deleted
   * here; each tab's provider drops it from whichever composition covered it,
   * and deletes that document when it covered nothing else.
   */
  const handleRemoveIdmp = useCallback((idmpId: string) => {
    setDocumentData(data =>
      data
        ? {
            ...data,
            medicinalProducts: data.medicinalProducts.map(r =>
              r.id === idmpId && !r.toBeDeleted
                ? { ...r, toBeDeleted: true }
                : r
            ),
          }
        : data
    );
    setIdmpRemove(prev => ({ seq: (prev?.seq ?? 0) + 1, idmpId }));
    setRemoveOpen(false);
  }, []);

  const handleAddIdmp = useCallback(
    (idmpId: string, compositionIdByTab: Record<string, string>) => {
      // Pin the product at the version the dialog just listed — `version` is
      // required on every reference, and this is the version the author chose.
      const chosen = candidates.find(c => c.id === idmpId);
      if (!chosen) return;
      const ref = {
        id: chosen.id,
        modelId: chosen.modelId,
        version: chosen.version,
      };
      setDocumentData(data =>
        data
          ? { ...data, medicinalProducts: [...data.medicinalProducts, ref] }
          : data
      );
      setIdmpAdd(prev => ({
        seq: (prev?.seq ?? 0) + 1,
        idmp: ref,
        compositionIdByTab,
      }));
      setAddOpen(false);
    },
    [candidates]
  );

  const items: TabsProps['items'] = TAB_CONFIGS.map(({ key, label }) => ({
    key,
    label,
    destroyOnHidden: false,
    forceRender: true,
    children: (
      <EpiProvider
        // Not a `key`: remounting would re-seed the compositions but also throw
        // away which document and section were open, which is the whole screen
        // jumping back to the top on every save.
        syncToken={syncedKey}
        initialCompositions={documentData?.[key as EpiDocumentType] ?? []}
        documentData={documentData}
        docType={key as EpiDocumentType}
        documentId={documentId}
        tabKey={key}
        idmpById={idmpById}
        idmpAdd={idmpAdd}
        idmpRemove={idmpRemove}
        epiImport={epiImport}
        readOnly={readOnly}
        canManageDocuments={canManageDocuments}
        canRenameDocuments={canRenameDocuments}
        canAddCustomSections={canAddCustomSections}
        lang={lang}
        onChange={onChange}
      >
        <EpiContainer docType={key as EpiDocumentType} />
      </EpiProvider>
    ),
  }));

  /**
   * Applies a read document. Each provider replaces its own tab's documents,
   * flagging the ones it replaces — the BE merges arrays by id, so a
   * composition merely left out of the payload is kept, not deleted.
   */
  const handleImport = useCallback(
    (byTab: Record<string, EpiComposition[]>) => {
      setEpiImport(current => ({
        seq: (current?.seq ?? 0) + 1,
        compositionsByTab: byTab,
      }));
      setImportOpen(false);
    },
    []
  );

  return (
    <>
      <HeaderBar>
        <DocumentHeader>
          <Select
            size="small"
            value={lang}
            onChange={setLang}
            options={SUPPORTED_LANGUAGES.map(l => ({
              value: l,
              label: LANGUAGE_LABELS[l],
            }))}
            style={{ minWidth: 130 }}
          />
          <Caption>
            {resolveInstanceName(
              // Read from the instance: the document may be save-ready, which
              // strips the enrichment this name comes from.
              instance?.data?.productFamily?.data?.name,
              lang
            ) ?? '—'}{' '}
            · Medicinal products ({docIdmp.length})
          </Caption>
          <Chips>
            {namedProducts.slice(0, VISIBLE_PRODUCTS).map(p => (
              <Tooltip key={p.id} title={p.name}>
                <Chip>{p.label}</Chip>
              </Tooltip>
            ))}
            {namedProducts.length > VISIBLE_PRODUCTS && (
              <MoreButton
                type="button"
                onClick={() => setProductsOpen(open => !open)}
              >
                {productsOpen
                  ? 'Hide'
                  : `+${namedProducts.length - VISIBLE_PRODUCTS} more`}
              </MoreButton>
            )}
          </Chips>
          {!readOnly && canAddProduct && familyId && (
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={() => setAddOpen(true)}
              disabled={!idmpModelId}
            >
              Add product
            </Button>
          )}
          {!readOnly && canRemoveProduct && (
            <Button
              size="small"
              type="text"
              icon={<MinusOutlined />}
              onClick={() => setRemoveOpen(true)}
              disabled={docIdmp.length < 2}
            >
              Remove product
            </Button>
          )}
          {!readOnly && canImport && (
            <Button
              size="small"
              type="text"
              icon={<ImportOutlined />}
              onClick={() => setImportOpen(true)}
              disabled={!instance?.modelVersionId}
            >
              Import document
            </Button>
          )}
          {canExport && (
            <ExportGroup>
              {/* The Tooltip wraps the pair rather than sitting between
                  Space.Compact and the Button: Space.Compact styles the
                  children it is handed, so anything in between leaves the two
                  controls sized and cornered independently. */}
              <Tooltip
                title={`Exports the ${LANGUAGE_LABELS[lang]} version — the language selected on the left.`}
              >
                <Space.Compact>
                  <Select
                    size="small"
                    value={exportFormat}
                    onChange={setExportFormat}
                    options={EXPORT_FORMATS}
                    style={{ minWidth: 110 }}
                  />
                  <Button
                    size="small"
                    icon={<DownloadOutlined />}
                    loading={isExporting}
                    disabled={!documentId}
                    onClick={() =>
                      exportEpi({
                        epiId: documentId,
                        format: exportFormat,
                        language: lang,
                        fallbackName: instanceName,
                      })
                    }
                  >
                    Export
                  </Button>
                </Space.Compact>
              </Tooltip>
            </ExportGroup>
          )}
        </DocumentHeader>

        {productsOpen && (
          <ProductList>
            {namedProducts.map(p => (
              <ProductRow key={p.id}>
                <ProductName title={p.name}>{p.name}</ProductName>
                <Version>v{p.version}</Version>
              </ProductRow>
            ))}
          </ProductList>
        )}
      </HeaderBar>

      <DocumentTabs defaultActiveKey={TAB_CONFIGS[0].key} items={items} />

      {removeOpen && (
        <RemoveIdmpModal
          isOpen
          products={namedProducts}
          tabs={removeTabs}
          onConfirm={handleRemoveIdmp}
          onCancel={() => setRemoveOpen(false)}
        />
      )}

      {importOpen && instance?.modelVersionId && (
        <ImportEpiModal
          isOpen
          modelVersionId={instance.modelVersionId}
          products={namedProducts.map(p => ({ id: p.id, name: p.name }))}
          productsById={Object.fromEntries(docIdmp.map(r => [r.id, r]))}
          currentByTab={Object.fromEntries(
            TAB_CONFIGS.map(({ key }) => [
              key,
              getStoredCompositions(documentId)[key] ??
                documentData?.[key as EpiDocumentType] ??
                [],
            ])
          )}
          languageLabels={LANGUAGE_LABELS}
          lang={lang}
          onConfirm={handleImport}
          onCancel={() => setImportOpen(false)}
        />
      )}

      {addOpen && (
        <AddIdmpModal
          isOpen
          candidates={candidates}
          isLoading={isLoadingFamilyIdmps}
          tabs={addTabs}
          lang={lang}
          onConfirm={handleAddIdmp}
          onCancel={() => setAddOpen(false)}
        />
      )}
    </>
  );
};

export default Epi;

/**
 * Underline tabs rather than antd's card type. The four documents are one ePI
 * seen four ways, not four filed things, and the card chrome said otherwise —
 * it also stacked a second border on top of the section cards below.
 */
const DocumentTabs = styled(Tabs)`
  .ant-tabs-nav {
    margin: 0;
    padding: 0 20px;
    background: ${({ theme }) => theme?.colors?.surface || '#ffffff'};
  }

  /* The documents sit on the page, not on the bar above them — that is what
     separates the controls from the content being edited. */
  .ant-tabs-content-holder {
    background: ${({ theme }) => theme?.colors?.background || '#f4f5f8'};
    padding: 20px;
  }

  .ant-tabs-tab {
    padding: 10px 4px !important;
    font-size: 15px;
    color: ${({ theme }) => theme?.colors?.textSecondary || '#475067'};
  }

  .ant-tabs-tab + .ant-tabs-tab {
    margin-left: 28px !important;
  }

  .ant-tabs-tab:hover .ant-tabs-tab-btn {
    color: ${({ theme }) => theme?.colors?.primaryHover || '#1d4ed8'};
  }

  /* Colour and the ink bar carry the active state. Weight would too, but antd
     does not reserve the bold width, so selecting a tab nudges its own label
     and every tab after it. */
  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${({ theme }) => theme?.colors?.primary || '#2563eb'} !important;
  }

  .ant-tabs-ink-bar {
    height: 2px;
    background: ${({ theme }) => theme?.colors?.primary || '#2563eb'};
  }
`;

const HeaderBar = styled.div`
  background: ${({ theme }) => theme?.colors?.surface || '#ffffff'};
  /* Closes the controls row off from the document tabs. Owned here rather than
     by the row inside, so an open product tray falls above the line. */
  border-bottom: 1px solid
    ${({ theme }) => theme?.colors?.borderLight || '#e6e8ee'};
`;

/**
 * Every product on the ePI, with the version each reference pins. Worth showing
 * because the chips resolve names through the *latest* instance, so a chip can
 * name a version this ePI does not document.
 */
const ProductList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 2px 24px;
  padding: 10px 20px 12px;
  /* A tray, not more of the bar: it is a disclosure, and without its own
     ground it read as another row of the header. */
  background: ${({ theme }) => theme?.colors?.background || '#f4f5f8'};
  border-top: 1px solid
    ${({ theme }) => theme?.colors?.borderLight || '#e6e8ee'};
`;

const ProductRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
  padding: 3px 0;
`;

const ProductName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  color: ${({ theme }) => theme?.colors?.textPrimary || '#0f1729'};
`;

const Version = styled.span`
  flex: none;
  font-size: 11px;
  color: ${({ theme }) => theme?.colors?.textSecondary || '#475067'};
`;

const MoreButton = styled.button`
  border: 1px solid ${({ theme }) => theme?.colors?.border || '#cbd5e1'};
  background: transparent;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  padding: 1px 8px;
  cursor: pointer;
  color: ${({ theme }) => theme?.colors?.textSecondary || '#475067'};

  &:hover {
    color: ${({ theme }) => theme?.colors?.primary || '#2563eb'};
    border-color: ${({ theme }) => theme?.colors?.primary || '#2563eb'};
  }
`;

const DocumentHeader = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 16px;
  /* HeaderBar supplies the surface; the expanded product list sits under this
     row inside the same bar. */
  padding: 12px 20px;
`;

/** Export belongs at the far end of the row, away from the product actions. */
const ExportGroup = styled.div`
  margin-left: auto;

  /* Space.Compact lines the borders up, not the boxes: the select sizes its
     inner selector and the button sizes itself, so the edge they share steps.
     One height for both — antd's own small control height. */
  .ant-select-selector,
  .ant-btn {
    height: 24px !important;
  }
`;

/**
 * Family and product count, in one voice — they name the same thing at two
 * scales, and styling them apart made the row look like two sections.
 */
const Caption = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme?.colors?.textSecondary || '#475067'};
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.span`
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
  font-weight: 600;
  line-height: 18px;
  padding: 1px 8px;
  border-radius: 4px;
  color: #0958d9;
  background: #e6f4ff;
  border: 1px solid #91caff;
`;
